import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Variants – a quick tip for better React components",
  "description": "Reusable components like to grow boolean props for different situations and complexity soon blows up. Variant props are the perfect solution.",
  "published": "2021-03-02T00:00:00.000Z",
  "categories": "Technical, React, Lessons",
  "hero": "./img/screenshot-1614701159308.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Say you're building a component that shows up in lots of places, like a header.`}</p>
    <p>{`Look at designs and sure enough, same header on every page. Logo, few buttons, username. You create a reusable component. `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Don%27t_repeat_yourself"
      }}>{`DRY`}</a>{` all the way.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`const Header = () => {
  return (
    <Box>
      <Logo />
      <MenuItem />
      <MenuItem />
      <UserMessages />
      <UserProfile />
    </Box>
  )
}
`}</code></pre>
    <p>{`Fantastic! You have a header that works everywhere.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media4.giphy.com/media/SWzVtsCPEPggXQsGoT/giphy-loop.mp4?cid=7cf28b0fnjnwk2hrp8s8og44ekvg7yworq2jg8gy9c9s7cst&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <h2 {...{
      "id": "a-new-page-appears"
    }}>{`A new page appears`}</h2>
    <p><em parentName="p">{`"Hey, header looks weird on homepage. Where's the signup button?"`}</em></p>
    <p>{`Your designer's not happy. Header that's the same everywhere can't be same on the homepage. That's for marketing, they need signups.`}</p>
    <p>{`obviously 🙄`}</p>
    <p>{`You add a prop.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`const Header = (props: { showSignup: boolean }) => {
  return (
    <Box>
      <Logo />
      <MenuItem />
      <MenuItem />
      {!props.showSignup && <UserMessages />}
      {!props.showSignup && <UserProfile />}
      {props.showSignup && <SignupButton />}
    </Box>
  )
}
`}</code></pre>
    <p>{`Cool, you've kept it DRY. Same header, same styling, signup yes or no.`}</p>
    <h2 {...{
      "id": "a-funnel-shows-up"
    }}>{`A funnel shows up`}</h2>
    <p><em parentName="p">{`"Hey, users are getting distracted out of this purchase funnel. Header too busy"`}</em></p>
    <p>{`Your universal header is costing you money. When users are on the path to paying, you want `}<em parentName="p">{`nothing`}</em>{` to stand in their way.`}</p>
    <p>{`Another boolean!`}</p>
    <p><em parentName="p">{`"Oh and let's pretend this is a fullscreen modal, add a close button. Keep the logo"`}</em></p>
    <p>{`Two booleans!`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "593px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/5f4c53e9877246cfc312dd2a8414a5cb/0b5b1/aqV7k1png2f49bb.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "70.85201793721974%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'284\\'%20viewBox=\\'0%200%20400%20284\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M142%206c-2%201-3%2025-1%2028%201%202%2013%203%2017%201%202-1%203-1%207%201%205%201%205%201%209-1l4-2%204%202c4%202%205%202%208%201l6-1h63c2-2%201-27%200-29-1-1-4-2-10-1a201%20201%200%2001-31-1l-7%201c-3%202-3%202-6%200h-12c-3-2-9-1-12%201-2%201-2%201-6-1-4-1-11-1-14%201-1%202-2%201-5%200-4-2-12-3-14%200m23%201c-4%202-4%203-4%2014s1%2013%208%2013%208-2%208-14c0-9%200-10-3-12s-6-3-9-1m17%201c-3%203-4%2020-1%2024%203%203%2010%203%2013-1s3-20-1-23c-3-3-8-3-11%200m-40%2012l1%2014h6c8%200%2010-1%2010-8l-2-5v-2l1-6c0-5-2-6-10-6h-6v13m56%201v13h6c5%200%205%200%205-3%200-2%200-3-2-3s-2-1-2-10V7h-7v14m13%200v13h10c9%200%2010-1%2010-3%200-3%202-3%202%200%200%202%201%203%204%203h4l-4-24c0-3%200-3-5-3h-6l-1%2011-1%2010h-3c-3%200-3%200-3-2s1-3%202-3c2%200%202-1%202-3s0-3-2-3-2-1-2-2c0-2%201-2%202-2%202%200%202-1%202-3%201-3%200-3-5-3h-6v14m31%200v13h3c3%200%203%200%203-5v-4l2%204c1%205%201%205%204%205h4V7h-7l1%205c0%205-1%204-3-2-1-2-2-3-4-3h-3v14m22%2062c0%203%203%206%204%205a4454%204454%200%2000-9%2083c-1-5-2-10-6-15-2-4-3-6-2-9%200-5-3-10-8-12-4-2-9%200-13%204-3%203-4%203-11%204-8%200-8%200-9-3-4-9-19-10-35-4-7%203-8%205-3%206l8%205%205%203-3%203-7%203c-10%200-12%2015-3%2019%202%202%203%203%204%208%200%204%202%208%205%2014l4%208-29%2017c-33%2020-35%2020-47%2018-5%200-6-1-13-9-19-24-18-23-17-25%203-3%202-7-1-14-4-11-22-28-28-28-2%200-4%204-4%207%200%202-5%203-9%203-4-1-5%200-3%204l1%203c-3%200-2%207%201%2012%2010%2018%2012%2020%2024%2020h8l8%2010a10108%2010108%200%200022%2030v19h19l5%206%205%206h7c7%200%207%200%202-6s-5-6%201-6c8%200%2011-4%206-9-1-1-2-4-2-11-1-10-1-11-6-12a633%20633%200%200151-33l2%204%205%209%205%208%203%208c2%204%202%205%200%206-2%202-1%206%201%209%202%201%202%203%202%2010s0%209%202%2010v13h73v-6c0-6%200-6%203-6h5c3%202%2010%201%2013-2s3-7%202-12c-1-3-2-5-1-7%203-8-7-14-16-10-2%202-3%201-6%200-4-2-4-1-9-21l-6-22c-3-10-3-13%201-44a2024%202024%200%20005-72c-5-2-6-1-6%201m-77%2052l-12%203-3%201%203%202%208%205c5%203%205%203%209%202l10-3c7-1%208-4%202-8-4-2-11-3-17-2m49%203c-6%202-7%2010-2%2014%207%207%2018-1%2013-10-2-5-6-6-11-4m-17%207c-14%202-18%202-23%204-12%203-15%206-12%2012%202%202%202%204%202%207-1%203-1%203%201%201l13-8c10-5%2012-5%2018-6%208%200%2010%201%2015%209%205%2010%208%2030%205%2030l-2%201%202%201%201%201h-1l-2%201%202%201%201%201-1%201c-2-1-7%203-7%205l-1%201-3%202-7%202c-4%201-4%201%202%201%207%200%2011-2%2011-4s3-5%205-5l1%202-2%201-2%202c-2%205-4%206-13%207-8%200-12%200-17-4-3-2-3-3-3-8%200-7-1-9-13-18-5-5-6-6-6-9v-3l-3%202-3%202%203%2013%203%206%206%2012c8%2014%2013%2018%2028%2019%209%200%209%200%2018-4l9-3%206-12%206-18%202-8c2-4-1-17-7-25l-2-4-4%202c-7%205-16%202-18-5-1-5-1-6-8-5m-49%2015c-7%208%204%2018%2011%2011%204-4%204-7%201-11-4-3-9-3-12%200m-119%209l-1%201-2%202c0%203-7%206-10%204-2%200-2%200-1%203%202%203%202%204%200%204s-2%200-2%203c1%203%207%2015%2011%2018l2%204c0%204%2023%204%2028-1%203-2%203-7%202-11l-3-7c-3-5-20-22-23-22l-1%202m97%2072c-3%202-3%205-1%208%201%202%202%205%202%2012%200%206%201%209%202%2010h12l18%201h16l1-3v-5c-2-2-2-4-1-6v-16c-2-2-47-3-49-1m121%202c-3%202-4%205-3%2015%200%2011%201%2012%2010%2012h6v-15h-9v-4c0-3%200-4%201-3l1%203c0%202%200%202%204%202%203%200%203%200%203-3%200-6-7-10-13-7m17%201c-2%201-2%202-2%206s0%205%206%208c1%202%201%202-2%202-4%200-4%200-4%203%200%205%202%207%208%207%209%200%2011-8%203-16-3-3-4-4-4-6%201-1%201-1%202%201%201%203%206%203%206-1%200-3-3-6-7-6l-6%202m-184%205l-2%2012c-2%209-2%209%203%209%203%200%204-1%204-3%200-3%202-3%202%201%200%202%200%202%204%202s4%200%204-2l-2-7-1-12-1-6h-11v6m16%208v13h6c5%200%205%200%205-3%200-2%200-3-2-3s-2-1-2-10v-11h-7v14m12-1l1%2014h11v-3c0-2%200-3-2-3s-2-1-2-10v-11h-8v13m18-10c0%202%200%203%202%203s2%201%202%2011v10h8v-10c0-10%200-11%202-11l2-3c0-3%200-3-8-3h-8v3m17%2011v13h7v-5l1-6%201%206v5h7v-27h-3c-4%200-4%200-4%205l-1%204-1-4c0-5%200-5-3-5h-4v14m19%200v13h12v-3c0-3%200-3-2-3s-3%200-3-2%201-3%202-3c2%200%202-1%202-3s0-3-2-3c-3%200-3-4%200-4%202%200%203-1%203-3%200-3%200-3-6-3h-6v14m18-11c0%202%201%203%203%203s2%200%202%2010v11h7v-10c0-10%200-11%202-11l2-3c0-3%200-3-8-3h-8v3m17%2011v13h7v-5l1-6%201%206v5h7v-27h-3c-4%200-4%200-4%205l-1%204-1-4c0-5%200-5-3-5h-4v14m19%200v13h7v-27h-7v14m10%200v13h3c3%200%203%200%203-4v-4l1%204c2%203%202%204%205%204h4v-27h-3c-4%200-4%200-4%204v5l-2-5c-1-3-2-4-4-4h-3v14\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/5f4c53e9877246cfc312dd2a8414a5cb/ca0a1/aqV7k1png2f49bb.webp 223w", "/static/5f4c53e9877246cfc312dd2a8414a5cb/75680/aqV7k1png2f49bb.webp 445w", "/static/5f4c53e9877246cfc312dd2a8414a5cb/ee627/aqV7k1png2f49bb.webp 593w"],
              "sizes": "(max-width: 593px) 100vw, 593px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/5f4c53e9877246cfc312dd2a8414a5cb/e92b6/aqV7k1png2f49bb.png 223w", "/static/5f4c53e9877246cfc312dd2a8414a5cb/e66bf/aqV7k1png2f49bb.png 445w", "/static/5f4c53e9877246cfc312dd2a8414a5cb/0b5b1/aqV7k1png2f49bb.png 593w"],
              "sizes": "(max-width: 593px) 100vw, 593px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/5f4c53e9877246cfc312dd2a8414a5cb/0b5b1/aqV7k1png2f49bb.png",
              "alt": "aqV7k1png2f49bb",
              "title": "aqV7k1png2f49bb",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`const Header = (props: {
  showSignup: boolean
  hideMenu: boolean
  showClose: boolean
}) => {
  return (
    <Box>
      <Logo />
      {!props.hideMenu && <MenuItem />}
      {!props.hideMenu && <MenuItem />}
      {!props.showSignup && <UserMessages />}
      {!props.showSignup && <UserProfile />}
      {props.showSignup && <SignupButton />}
      {props.showClose && <CloseButton />}
    </Box>
  )
}
`}</code></pre>
    <p>{`Well that's not confusing at all. With 3 booleans, your header has 8 possible incantations.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/47d57500c2038e2c0f1bc87f6c0c426d/a6ec4/Possible-incantations-of-Header-component2cg616.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "81.1659192825112%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'325\\'%20viewBox=\\'0%200%20400%20325\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M288%206a2091%202091%200%2000-2%2045l-68%201h-68v-9c0-13-1-28-2-27v36h-24c-25%200-25%200-24-2v-3l-1-4-3-3-3%201c-3%200-2%203%202%204s4%205%201%206c-4%201-6-1-6-3v-2l-1%203v3H53a221%20221%200%2000-48%203%20480%20480%200%200179-1h64l1%2011a1627%201627%200%2001-7%20169%201092%201092%200%20002%2038c0-46%200-50%202-76%205-53%205-55%205-100l-1-42h43a3592%203592%200%200093-1%202102%202102%200%2001-7%20141%20933%20933%200%20004-31l5-96V52h32a1790%201790%200%2001-16-2l-16%201%201-13a5271%205271%200%2001-1-32m-78%2058c-6%203-7%207-2%209%207%203%2014-1%2011-6-2-4-4-5-9-3M62%2067c-4%203-4%205%200%207%203%202%2013-2%2011-4l-8-6-3%203m263-2l-3%201-4%202c-2%202%201%205%208%206s10-5%205-9h-6M60%2089c-3%203-4%207-2%208%203%201%2012-2%2013-4%201-6-7-9-11-4m140%200c-6%206-3%209%207%207%205-2%207-5%203-8-2-3-7-3-10%201m118%2022c-2%200-1%206%201%206%204%202%2010%203%2011%201%203-4-6-10-12-7m-261%206l-3%205c0%203%2014%201%2015-3%201-1-4-6-7-6l-5%204m1%2024c-2%201-3%207-2%208h7c5%200%206-1%206-3%200-4-7-8-11-5m256%2025c-7%204-4%208%206%207%204-1%205-4%203-7-3-3-5-3-9%200m-123%2027c-5%204-1%207%207%207%204%200%205-1%205-3%200-5-9-8-12-4m114%2027c-3%203-3%207%200%208%203%202%208%200%2010-2s2-2-1-5c-4-5-6-5-9-1\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/47d57500c2038e2c0f1bc87f6c0c426d/ca0a1/Possible-incantations-of-Header-component2cg616.webp 223w", "/static/47d57500c2038e2c0f1bc87f6c0c426d/75680/Possible-incantations-of-Header-component2cg616.webp 445w", "/static/47d57500c2038e2c0f1bc87f6c0c426d/8d1ba/Possible-incantations-of-Header-component2cg616.webp 890w", "/static/47d57500c2038e2c0f1bc87f6c0c426d/3838e/Possible-incantations-of-Header-component2cg616.webp 1335w", "/static/47d57500c2038e2c0f1bc87f6c0c426d/e12b1/Possible-incantations-of-Header-component2cg616.webp 1438w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/47d57500c2038e2c0f1bc87f6c0c426d/e92b6/Possible-incantations-of-Header-component2cg616.png 223w", "/static/47d57500c2038e2c0f1bc87f6c0c426d/e66bf/Possible-incantations-of-Header-component2cg616.png 445w", "/static/47d57500c2038e2c0f1bc87f6c0c426d/4ef49/Possible-incantations-of-Header-component2cg616.png 890w", "/static/47d57500c2038e2c0f1bc87f6c0c426d/4e814/Possible-incantations-of-Header-component2cg616.png 1335w", "/static/47d57500c2038e2c0f1bc87f6c0c426d/a6ec4/Possible-incantations-of-Header-component2cg616.png 1438w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/47d57500c2038e2c0f1bc87f6c0c426d/4ef49/Possible-incantations-of-Header-component2cg616.png",
                "alt": "Possible incantations of Header component",
                "title": "Possible incantations of Header component",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Possible incantations of Header component`}</figcaption>{`
  `}</figure></p>
    <p>{`Add one more ask from design and you're up to 16.`}</p>
    <p>{`😅`}</p>
    <h2 {...{
      "id": "youve-created-a-mess"
    }}>{`You've created a mess`}</h2>
    <p>{`Your beautiful universal header component is hard to use. Complexity is exploding, the code is getting hairy, and you're the only person on your team who knows how to hold it.`}</p>
    <p>{`For everyone else it's frustrating as heck. Get it right for this screen, breaks on that screen.`}</p>
    <p>{`Debugging feels like whack-a-mole.`}</p>
    <p>{`And I'm showing you a simple example. In production code these booleans start interacting. If `}<inlineCode parentName="p">{`hideMenu and showSignup`}</inlineCode>{` then do X, otherwise if not `}<inlineCode parentName="p">{`showClose`}</inlineCode>{` do Y.`}</p>
    <p>{`Nobody wants to touch your component ever again. It's too tricky.`}</p>
    <h2 {...{
      "id": "variants-to-the-rescue"
    }}>{`Variants to the rescue`}</h2>
    <p>{`How many of those 8 incantations are valid?`}</p>
    <p>{`I count 3.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/66bff6e0e436a3ec25aff3dc15670341/d38ac/Identify-your-variants690727.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "67.2645739910314%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'269\\'%20viewBox=\\'0%200%20400%20269\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M238%2010l-1%2019v13h-14l-56%201h-42v-4l-1-15-1-11v30h-20c-19%200-20%200-19-2l-1-2-1-3c0-2-1-2-3-2-4%201-4%202-1%203%205%201%205%205%200%205-3%200-3%200-3-2v-2l-1%203c0%202%200%202-27%202a173%20173%200%2000-42%203c-2%202%203%202%208%201%204-2%2013-2%2057-2%2051%200%2053%200%2053%202s-1%202-21%202l-24%202c-3%201%208%201%2034-1h11v15H49l43%201h31v38l-15%201c-37%203-57%206-67%2012l-5%203%206-2c4-3%2010-4%207-2l-3%207c0%202%2010%201%2011-1%202-2%200-4-4-6l-3-2%207-1c11-3%2054-8%2065-8l1%2011v12h-21l-34%202c-29%203-36%201-35-5%201-4%201-4-1-1s1%207%207%208l4%201-6%203-7%203c-1%201-1%201%201%201%205-3%2015-6%2018-6l3-1v4a187%20187%200%2001-1%208l2-8v-3h18l35%202h16l-1%207-1%209-43%201c-55-1-58-2-53-8l1-3-4%207c1%204%206%204%2054%205h45v6a1497%201497%200%20002%200v-6l17%201h74l18-1v47c0%2044%200%2064%202%2063v-97l1-13%207-1a397%20397%200%2000-2-1h-5v-9l1-9%2016-2a149%20149%200%200153%204c-3-2-19-5-24-5l-4-1%203-1c6%200%2022-3%2027-5l5-5c1-5-5-7-29-10a568%20568%200%2001-35-3l-11-1%201-15%201-21v-5h8l30-1c29%200%2056-3%2053-7l-2%201a221%20221%200%2001-54%204c3-1%204-4%201-6-2-2-4-3-8%200-5%202-5%204%202%206l-14%201-16%201v-9l1-9a785%20785%200%200148%202c-26-2-41-5-20-4%2024%202%2042%202%2040%201l-35-2h-33V5l-1%205M125%2047c0%202%203%203%2027%201a1683%201683%200%200113-3c-39%200-40%200-40%202m89-1l-74%204h-15v15h44l56-2h11v-9l1-9h-7l-16%201m-3%2019l-51%201h-35v38l20-1%2021-1%201-4c0-3%201-5%202-5v6l-1%204h7l33%202h26v-5l1-21%201-15h-4l-21%201m-79%2040h-7v23l6%201h102v-8l1-12v-3h-20a518%20518%200%2001-48%200h-1c0-2-12-2-33-1m104%209l-1%2012v3h17c28%200%2056-5%2057-10%202-5-8-7-41-8l-28-2%2024%201%203-1-10-1-15-2h-6v8M87%20130c-31%201-33%203-5%203l31%202h9v-6h-6l-29%201m37%203a3065%203065%200%2001108-2c1-1-20-1-53-1-55%201-55%201-55%203m94%201a1604%201604%200%2001-94%202l-1%209-1%208%2029%201c26%201%2056%200%2076-2h5v-9l1-9h-15\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/66bff6e0e436a3ec25aff3dc15670341/ca0a1/Identify-your-variants690727.webp 223w", "/static/66bff6e0e436a3ec25aff3dc15670341/75680/Identify-your-variants690727.webp 445w", "/static/66bff6e0e436a3ec25aff3dc15670341/8d1ba/Identify-your-variants690727.webp 890w", "/static/66bff6e0e436a3ec25aff3dc15670341/3838e/Identify-your-variants690727.webp 1335w", "/static/66bff6e0e436a3ec25aff3dc15670341/7e696/Identify-your-variants690727.webp 1744w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/66bff6e0e436a3ec25aff3dc15670341/e92b6/Identify-your-variants690727.png 223w", "/static/66bff6e0e436a3ec25aff3dc15670341/e66bf/Identify-your-variants690727.png 445w", "/static/66bff6e0e436a3ec25aff3dc15670341/4ef49/Identify-your-variants690727.png 890w", "/static/66bff6e0e436a3ec25aff3dc15670341/4e814/Identify-your-variants690727.png 1335w", "/static/66bff6e0e436a3ec25aff3dc15670341/d38ac/Identify-your-variants690727.png 1744w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/66bff6e0e436a3ec25aff3dc15670341/4ef49/Identify-your-variants690727.png",
                "alt": "Identify your variants",
                "title": "Identify your variants",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Identify your variants`}</figcaption>{`
  `}</figure></p>
    <p>{`Then why allow the other 5? If 5 out of 8 ways to use your component are bugs, something's wrong.`}</p>
    <p>{`Here's what you do 👉 turn those flags into a variant prop. Use TypeScript and you even get autocomplete 😍`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`const Header = (props: { variant: "homepage" | "funnel" }) => {
  let hideMenu, showClose, showSignup

  switch (variant) {
    case "homepage":
      showSignup = true
    case "funnel":
      hideMenu = true
      showClose = true
  }

  return (
    <Box>
      <Logo />
      {!hideMenu && <MenuItem />}
      {!hideMenu && <MenuItem />}
      {!showSignup && <UserMessages />}
      {!showSignup && <UserProfile />}
      {showSignup && <SignupButton />}
      {showClose && <CloseButton />}
    </Box>
  )
}
`}</code></pre>
    <p>{`Now the rest of your team can use your header with ease: `}<inlineCode parentName="p">{`<Header variant="..." />`}</inlineCode>{`. And thanks to TypeScript their IDE tells them what's available.`}</p>
    <p>{`AND we found a bug in my pseudocode. You don't want user menus showing up in funnel headers 😅`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: quick tip for shorter emails 👉 break your wrist`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`And then I broke my wrist a little 😅 `}<a parentName="p" {...{
          "href": "https://t.co/cQDBEqsTSQ"
        }}>{`pic.twitter.com/cQDBEqsTSQ`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1365789410306891777"
      }}>{`February 27, 2021`}</a></blockquote>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      